import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axiosInstance';
import {
    Container, Grid, Paper, Typography, Box, IconButton, Button, TextField,
    List, ListItem, ListItemText, CircularProgress, ListItemIcon, Chip, Tooltip
} from '@mui/material';
import {
    ArrowBack as BackIcon, Save as SaveIcon,
    Person as PersonIcon, MilitaryTech as MedalIcon
} from '@mui/icons-material';
import { useSnackbar } from '../context/SnackbarContext';

const ALL_YEARS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const getApplicableYears = (yearStr) => {
    if (!yearStr) return [];
    const normalised = String(yearStr).toUpperCase().trim();
    if (normalised.includes('-')) {
        const [start, end] = normalised.split('-');
        const startIndex = ALL_YEARS.indexOf(start.trim());
        const endIndex = ALL_YEARS.indexOf(end.trim());
        return ALL_YEARS.slice(startIndex, endIndex + 1);
    }
    if (normalised.includes(',')) {
        return normalised.split(',').map(y => y.trim());
    }
    return [normalised];
};

export default function Nominate() {
    const { ceremonyId } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();

    const [ceremony, setCeremony] = useState(state?.ceremonyName ? { name: state.ceremonyName } : null);
    const [awards, setAwards] = useState([]);
    const [students, setStudents] = useState([]);
    const [studentSchema, setStudentSchema] = useState([]);
    const [loading, setLoading] = useState(true);

    const [activeYear, setActiveYear] = useState(null);
    const [studentSearch, setStudentSearch] = useState("");
    const [selectedAwardKey, setSelectedAwardKey] = useState(null);
    const [nominations, setNominations] = useState({});
    const [existing, setExisting] = useState([]);
    const [saving, setSaving] = useState(false);
    const showSnackbar = useSnackbar();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [ceremonyRes, settingsRes, awardsRes, studentsRes, existingRes] = await Promise.all([
                api.get(`/ceremonies/${ceremonyId}`),
                api.get('/settings'),
                api.get('/awards'),
                api.get('/students'),
                api.get(`/ceremonies/${ceremonyId}/nominations`)
            ]);
            setExisting(Array.isArray(existingRes.data) ? existingRes.data : []);

            setCeremony(ceremonyRes.data);

            // Handle JSON parsing for schema if it's a string
            const schema = settingsRes.data.student_import_schema;
            setStudentSchema(typeof schema === 'string' ? JSON.parse(schema) : schema || []);

            // Only offer the awards linked to this ceremony (fall back to all if none linked yet)
            const linked = (ceremonyRes.data.award_ids || []).map(String);
            const allAwards = awardsRes.data.map(a => ({ db_id: a.id, ...a.payload }));
            setAwards(linked.length > 0 ? allAwards.filter(a => linked.includes(String(a.db_id))) : allAwards);
            setStudents(studentsRes.data.map(s => ({ db_id: s.id, ...s.payload })));


            // // Process Ceremony (Index 0)
            // if (results[0].status === 'fulfilled' && results[0].value.ok) {
            //     const data = await results[0].value.json();
            //     setCeremony(data);
            // }

            // // Process Settings (Index 1)
            // if (results[1].status === 'fulfilled' && results[1].value.ok) {
            //     const data = await results[1].value.json();
            //     setStudentSchema(typeof data.student_import_schema === 'string'
            //         ? JSON.parse(data.student_import_schema)
            //         : data.student_import_schema || []);
            // }

            // // Process Awards (Index 2)
            // if (results[2].status === 'fulfilled' && results[2].value.ok) {
            //     const data = await results[2].value.json();
            //     setAwards(data.map(a => ({ db_id: a.id, ...a.payload })));
            // }

            // // Process Students (Index 3)
            // if (results[3].status === 'fulfilled' && results[3].value.ok) {
            //     const data = await results[3].value.json();
            //     setStudents(data.map(s => ({ db_id: s.id, ...s.payload })));
            // }

        } catch (err) {
            console.error("Critical fetch error:", err);
            showSnackbar(`Critical fetch error:\n${err}`, 'error');
        } finally {
            setLoading(false); // Only hide when everything is done
        }
    }, [ceremonyId, showSnackbar]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // useEffect(() => {
    //     setStudentSearch("");
    // }, [activeYear]);

    // 1. Wrap the helper in useCallback
    const getStudentKey = useCallback((labelPart) =>
        studentSchema.find(s => s.label.toLowerCase().includes(labelPart.toLowerCase()))?.id,
        [studentSchema]);

    // const sFirstKey = getStudentKey("FirstName") || "id_4";
    // const sLastKey = getStudentKey("Surname") || "id_5";
    // const sYearKey = getStudentKey("Year Level") || "stu_1771908558902_zzte5";

    // Use memo to prevent these keys from being re-calculated on every tiny render
    const keys = useMemo(() => ({
        first: getStudentKey("FirstName") || "id_4",
        last: getStudentKey("Surname") || "id_5",
        year: getStudentKey("Year Level") || "stu_1771908558902_zzte5"
    }), [getStudentKey]);

    // Action: Assign Student using a unique Award+Year key
    const handleSelectStudent = (student) => {
        if (!selectedAwardKey) return;
        setNominations(prev => ({
            ...prev,
            [selectedAwardKey]: { student, reason: prev[selectedAwardKey]?.reason || '' }
        }));
        setSelectedAwardKey(null);
    };

    const removeNomination = (e, uniqueKey) => {
        e.stopPropagation();
        setNominations(prev => {
            const next = { ...prev };
            delete next[uniqueKey];
            return next;
        });
    };

    const filteredStudents = useMemo(() => {
        if (!Array.isArray(students)) return [];
        return students.filter(s => {
            const matchesYear = activeYear ? String(s[keys.year]) === String(activeYear) : true;
            const fullName = `${s[keys.first] || ''} ${s[keys.last] || ''}`.toLowerCase();
            return matchesYear && fullName.includes(studentSearch.toLowerCase());
        });
    }, [students, activeYear, studentSearch, keys]);

    const setReason = (uniqueKey, reason) => {
        setNominations(prev => ({ ...prev, [uniqueKey]: { ...prev[uniqueKey], reason } }));
    };

    // Count of saved nominations per "year-awardId" key
    const existingCounts = useMemo(() => existing.reduce((acc, n) => {
        const k = `${n.year_level}-${n.award_id}`;
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {}), [existing]);

    // Every nomination per student (saved and not yet saved), for the medals in the student list
    const studentNominations = useMemo(() => {
        const awardName = (id) => awards.find(a => String(a.db_id) === String(id))?.aw_1 || 'Award';
        const map = {};
        const add = (studentId, entry) => {
            if (!studentId) return;
            (map[studentId] = map[studentId] || []).push(entry);
        };
        existing.forEach(n => add(n.student_id, {
            label: `${n.award_name || n.award_payload?.aw_1 || awardName(n.award_id)} (Year ${n.year_level})`,
            saved: true
        }));
        Object.entries(nominations).forEach(([uniqueKey, nom]) => {
            const dash = uniqueKey.indexOf('-');
            add(nom.student?.db_id, {
                label: `${awardName(uniqueKey.slice(dash + 1))} (Year ${uniqueKey.slice(0, dash)}), not saved yet`,
                saved: false
            });
        });
        return map;
    }, [existing, nominations, awards]);

    const isLocked = ceremony?.cutoff_date ? new Date() > new Date(ceremony.cutoff_date) : false;

    const handleSaveAll = async () => {
        const entries = Object.entries(nominations);
        if (entries.length === 0) return;
        setSaving(true);
        try {
            const payload = entries.map(([uniqueKey, nom]) => {
                const dash = uniqueKey.indexOf('-');
                return {
                    year_level: uniqueKey.slice(0, dash),
                    award_id: uniqueKey.slice(dash + 1),
                    student_id: nom.student.db_id,
                    reason: nom.reason?.trim() || null
                };
            });
            const res = await api.post(`/ceremonies/${ceremonyId}/nominations`, { nominations: payload });
            const { created = 0, skipped = 0 } = res.data || {};
            showSnackbar(`${created} nomination(s) saved${skipped ? `, ${skipped} already existed` : ''}.`, 'success');
            setNominations({});
            const refreshed = await api.get(`/ceremonies/${ceremonyId}/nominations`);
            setExisting(Array.isArray(refreshed.data) ? refreshed.data : []);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to save nominations', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 15
                }}
            >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* 1. The Background Track (Static) */}
                    <CircularProgress
                        variant="determinate"
                        sx={{ color: 'grey.200' }}
                        size={80}
                        thickness={6}
                        value={100}
                    />
                    {/* 2. The Active Spinner (Moving) */}
                    <CircularProgress
                        variant="indeterminate"
                        disableShrink
                        sx={{
                            color: 'primary.main',
                            animationDuration: '600ms',
                            position: 'absolute',
                            left: 0,
                            [`& .MuiCircularProgress-circle`]: {
                                strokeLinecap: 'round',
                            },
                        }}
                        size={80}
                        thickness={6}
                    />
                </Box>

                {/* 3. Status Text */}
                <Typography
                    variant="h6"
                    sx={{ mt: 3, fontWeight: 500, color: 'text.secondary', letterSpacing: 1 }}
                >
                    LOADING NOMINATION DATA...
                </Typography>
            </Box>
        );
    }


    return (
        <Container maxWidth={false} disableGutters sx={{ mt: 3, mb: 4, px: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/ceremonies/${ceremonyId}`)}><BackIcon /></IconButton>
                    <Typography variant="h4" fontWeight="bold">
                        Nominate students for '{ceremony?.name || state?.ceremonyName || 'Ceremony'}'
                    </Typography>
                </Box>
                <Button
                    disabled={saving || isLocked || Object.keys(nominations).length === 0}
                    onClick={handleSaveAll}
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}>
                    {isLocked ? 'Nominations closed' : `Save All (${Object.keys(nominations).length})`}
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* LEFT COLUMN: AWARDS */}
                <Grid size={6}>
                    <Box sx={{ width: '100%' }}>
                        {ALL_YEARS.map(year => {
                            const yearAwards = awards.filter(a =>
                                getApplicableYears(a['aw_3']).includes(year)
                            );

                            if (yearAwards.length === 0) return null;

                            return (
                                <Paper key={year} sx={{ mb: 3, width: '100%', overflow: 'hidden' }}>
                                    <Box
                                        sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                                        onClick={() => setActiveYear(year === activeYear ? null : year)}
                                    >
                                        <Typography variant="subtitle1" fontWeight="bold">YEAR {year}</Typography>
                                    </Box>
                                    <List disablePadding>
                                        {yearAwards.map(award => {
                                            const uniqueKey = `${year}-${award.db_id}`;
                                            const nominee = nominations[uniqueKey];
                                            const isActive = selectedAwardKey === uniqueKey;

                                            return (
                                                <ListItem
                                                    key={uniqueKey}
                                                    divider
                                                    sx={{
                                                        bgcolor: isActive ? '#fffde7' : 'inherit',
                                                        py: 1.5,
                                                        px: 2,
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: isActive ? '#fffde7' : '#fafafa' }
                                                    }}
                                                    onClick={() => {
                                                        setSelectedAwardKey(uniqueKey);
                                                        setActiveYear(year); // This ensures the right column filters to the correct year
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={award['aw_1']}
                                                        secondary={existingCounts[uniqueKey] ? `${existingCounts[uniqueKey]} saved nomination(s)` : null}
                                                        primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                                                    />
                                                    <Box sx={{
                                                        width: '50%', // Fixed relative width for the "red box" area
                                                        minHeight: 48,
                                                        border: nominee ? '1px solid #4caf50' : (isActive ? '2px solid #1976d2' : '1px dashed #ccc'),
                                                        borderRadius: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 1,
                                                        bgcolor: nominee ? '#f1f8e9' : 'white',
                                                        ml: 2
                                                    }}>
                                                        {nominee ? (
                                                            <Box sx={{ width: '100%', py: 0.5 }}>
                                                                <Chip
                                                                    label={`${nominee.student[keys.first]} ${nominee.student[keys.last]}`}
                                                                    onDelete={(e) => removeNomination(e, uniqueKey)}
                                                                    color="success"
                                                                    size="small"
                                                                    sx={{ width: '100%', justifyContent: 'space-between' }}
                                                                />
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    multiline
                                                                    maxRows={4}
                                                                    placeholder="Reason (optional)"
                                                                    value={nominee.reason || ''}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => setReason(uniqueKey, e.target.value)}
                                                                    sx={{ mt: 0.5, bgcolor: 'white' }}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color={isActive ? "primary" : "text.secondary"} sx={{ textAlign: 'center', width: '100%' }}>
                                                                {isActive ? "SELECT STUDENT ON RIGHT →" : "Click to nominate..."}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Paper>
                            );
                        })}
                    </Box>
                </Grid>

                {/* RIGHT COLUMN: STUDENTS */}
                <Grid size={6}>
                    <Box sx={{ position: 'sticky', top: 20, width: '100%' }}>
                        <TextField
                            fullWidth
                            size="small" // Made smaller to match the "narrower" feel
                            value={studentSearch}
                            onChange={(e) => setStudentSearch(e.target.value)}
                            sx={{ mb: 2, bgcolor: 'white' }}
                            placeholder="Search Students..."
                        />
                        <Paper sx={{ height: '75vh', overflow: 'auto', width: '100%' }}>
                            <Box sx={{ p: 2, bgcolor: selectedAwardKey ? '#1976d2' : '#f5f5f5', color: selectedAwardKey ? 'white' : 'inherit', transition: '0.2s' }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {selectedAwardKey ? "STEP 2: SELECT A STUDENT" : "ELIGIBLE STUDENTS"}
                                </Typography>
                            </Box>
                            <List disablePadding>
                                {filteredStudents.map(student => (
                                    <ListItem
                                        key={student.db_id}
                                        divider
                                        sx={{
                                            py: 0.5, // Reduced padding for a narrower row
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: '#f0f7ff' },
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        onClick={() => handleSelectStudent(student)}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <PersonIcon
                                                fontSize="small"
                                                color={selectedAwardKey ? "primary" : "inherit"}
                                            />
                                        </ListItemIcon>

                                        {/* <ListItemText
                                            primary={`${student[sFirstKey]} ${student[sLastKey]}`}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                fontWeight: 500,
                                                sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
                                            }}
                                        />

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                ml: 'auto',
                                                fontWeight: 'bold',
                                                color: 'text.secondary',
                                                minWidth: 'fit-content',
                                                pl: 2
                                            }}
                                        >
                                            Year {student[sYearKey]}
                                        </Typography> */}

                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                        >
                                            {`${student[keys.first]} ${student[keys.last]}`}
                                        </Typography>
                                        {(() => {
                                            const noms = studentNominations[student.db_id] || [];
                                            if (noms.length === 0) return null;
                                            return (
                                                <Tooltip
                                                    arrow
                                                    title={
                                                        <Box>
                                                            <Typography variant="caption" fontWeight="bold" display="block">
                                                                {noms.length} nomination{noms.length > 1 ? 's' : ''}
                                                            </Typography>
                                                            {noms.map((n, i) => (
                                                                <Typography key={i} variant="caption" display="block">{n.label}</Typography>
                                                            ))}
                                                        </Box>
                                                    }
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0 }}>
                                                        {noms.slice(0, 3).map((n, i) => (
                                                            <MedalIcon
                                                                key={i}
                                                                sx={{ fontSize: 18, ml: i === 0 ? 0 : -0.5, color: n.saved ? '#f9a825' : '#66bb6a' }}
                                                            />
                                                        ))}
                                                        {noms.length > 3 && (
                                                            <Typography variant="caption" fontWeight="bold" sx={{ color: '#f9a825', ml: 0.25 }}>
                                                                +{noms.length - 3}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })()}
                                        <Typography
                                            variant="caption"
                                            sx={{ ml: 'auto', fontWeight: 'bold', color: 'text.secondary', pl: 2 }}
                                        >
                                            Year {student[keys.year]}
                                        </Typography>

                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    );

}